import { useCallback } from "react";
import {
  importBlendAndLoad,
  importMeshFileAndSpawn,
} from "./importToLibrary.ts";
import { canAllocate, showMemoryToast } from "./memoryGovernor.ts";
import { isMeshImportFileName } from "./meshImportFormats.ts";
import { compressAndStoreReferenceImage } from "./referenceImageUpload.ts";
import { useStore } from "./store.ts";

/**
 * Heuristic multiplier for predicted in-memory footprint of a parsed mesh
 * file. OBJ/glTF/FBX inflate into typed arrays (positions, normals, indices),
 * BVH acceleration, GPU upload buffers, and undo snapshots — empirically a
 * 5-10× factor over the source file size. We use 6× to refuse early at
 * `over` while still admitting borderline-size imports under normal pressure.
 */
const IMPORT_HEAP_MULTIPLIER = 6;

/**
 * Hook that returns a callback to handle loading a dropped/selected file.
 * Supports .blend, mesh files (.obj/.glb/.gltf/.fbx), and image files.
 *
 * Mesh files and Blend files are imported to the user's library (deduplicated by
 * content hash) and loaded into the scene in a single unified flow. The memory
 * governor is consulted *before* parsing so a refusal does not waste the parse
 * cost — refused imports surface a deduplicated error toast.
 */
export function useFileLoader() {
  const addReferenceLayer = useStore((s) => s.addReferenceLayer);
  const setWorkspaceMode = useStore((s) => s.setWorkspaceMode);

  return useCallback(
    async (file: File) => {
      const name = file.name.toLowerCase();

      if (isMeshImportFileName(name) || name.endsWith(".blend")) {
        if (!canAllocate(file.size * IMPORT_HEAP_MULTIPLIER)) {
          showMemoryToast(
            "Import cancelled — memory limit reached. Remove objects, undo, or raise the limit in Settings.",
            {
              severity: "error",
              duration: 6000,
              dedupKey: "memory-import-refused",
            },
          );
          return;
        }
        if (isMeshImportFileName(name)) {
          await importMeshFileAndSpawn(file);
        } else {
          await importBlendAndLoad(file, "load");
        }
        return;
      }

      if (file.type.startsWith("image/")) {
        try {
          const { imageKey, imageUrl } = await compressAndStoreReferenceImage(
            file,
            file.name.replace(/\.[^.]+$/, ""),
          );
          addReferenceLayer(imageKey, imageUrl);
          setWorkspaceMode("reference");
        } catch (err) {
          console.error("Failed to upload reference image:", err);
        }
      }
    },
    [addReferenceLayer, setWorkspaceMode],
  );
}
